/**
 * Team kit config for SoccerKit. Prefer DB kit (team.kitPrimary etc. from API);
 * otherwise lookup by normalized team name in TEAM_KIT_BY_NAME; else grey default.
 */

import type { TeamKitConfig } from '../constants/teamKitColors';
import { DEFAULT_TEAM_KIT_CONFIG, normalizeTeamKitType, TEAM_KIT_BY_NAME } from '../constants/teamKitColors';

/** Short display names → keys in TEAM_KIT_BY_NAME (normalized). */
const KIT_NAME_ALIASES: Record<string, string> = {
  'man city': 'manchester city',
  /** World Cup / FIFA naming vs `TEAM_KIT_BY_NAME` key */
  'south korea': 'korea republic',
  'republic of korea': 'korea republic',
};

/** Normalize for kit lookup: lowercase, trim, collapse spaces, strip diacritics (e.g. ö→o). */
export function normalizeTeamNameForKitLookup(name: string): string {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim().toLowerCase().replace(/\s+/g, ' ').trim();
  const withoutDiacritics = trimmed.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  return withoutDiacritics;
}

/** Team shape from API when kit fields are included. */
export interface TeamWithOptionalKit {
  name: string;
  kitPrimary?: string | null;
  kitSecondary?: string | null;
  kitType?: string | null;
}

/** Returns kit config from team object when kit fields are set (from DB), else grey default. */
export function getTeamKitConfigFromTeam(team: TeamWithOptionalKit): TeamKitConfig {
  if (
    team.kitPrimary != null &&
    team.kitSecondary != null &&
    team.kitPrimary !== '' &&
    team.kitSecondary !== ''
  ) {
    return {
      primary: team.kitPrimary,
      secondary: team.kitSecondary,
      type: normalizeTeamKitType(team.kitType ?? 'plain'),
    };
  }
  return DEFAULT_TEAM_KIT_CONFIG;
}

/**
 * Returns kit config. Pass a team object (with optional kitPrimary/kitSecondary/kitType from API)
 * to use DB kit when present; otherwise lookup by normalized name in TEAM_KIT_BY_NAME; else grey default. Never null.
 */
export function getTeamKitConfig(teamNameOrTeam: string | TeamWithOptionalKit): TeamKitConfig {
  if (typeof teamNameOrTeam !== 'string') {
    return getTeamKitConfigFromTeam(teamNameOrTeam);
  }
  const key = normalizeTeamNameForKitLookup(teamNameOrTeam);
  let fromMap = key ? TEAM_KIT_BY_NAME[key] : undefined;
  if (fromMap === undefined && key !== '') {
    const aliased = KIT_NAME_ALIASES[key];
    if (aliased !== undefined) {
      fromMap = TEAM_KIT_BY_NAME[aliased];
    }
  }
  return fromMap ?? DEFAULT_TEAM_KIT_CONFIG;
}

/** Returns display name for team (string or team object with name). */
export function getTeamDisplayName(teamNameOrTeam: string | TeamWithOptionalKit): string {
  return typeof teamNameOrTeam === 'string' ? teamNameOrTeam : teamNameOrTeam.name;
}
